/**
 * leerText — der Grund einer leeren Optionsliste, an EINER Stelle.
 *
 * @description
 *   `BottomSheetSelect` und `BottomSheetMultiSelect` tragen dieselbe
 *   Optionsflaeche und brauchen denselben Satz. Bis zur Wiedervorlage 3 stand
 *   die Tabelle `LEER_TEXT` wortgleich in BEIDEN Dateien — zwei Wahrheiten fuer
 *   eine Aussage (R15-W5). Sie liegt jetzt hier.
 *
 *   Der Grund ist eine GESCHLOSSENE Menge von vier Werten. Drei davon sind die
 *   Woerter der Hausform `EmptyState` (G0.2) — `no-data`, `error`, `unknown` —,
 *   das vierte ist `loading`: `EmptyState` kennt keinen Ladezustand (sein
 *   `@dontUseFor` verweist auf `Skeletons/`), eine Optionsliste kennt ihn.
 *   🔴 `loading` traegt darum auch kein `data-reason` (R16-W1, Wiedervorlage 4):
 *   der SATZ bleibt, das Wort faellt — es ist kein Leerzustand.
 *
 *   🔴 `unknown` ist ein EIGENER Grund mit eigenem Satz, kein Rueckfall.
 *   „Unbekannt ist nie 0 und nie eine leere Pille" (Konzept §8.1 / N8.3): wer
 *   nicht weiss, ob die Quelle geliefert hat, darf nicht „es gibt nichts"
 *   behaupten. Die erste Fassung fuehrte nur drei Eintraege und bildete den
 *   vierten still auf `ready` = „No options" ab — genau die ausgeschlossene
 *   Aussage (R16-C1).
 *
 *   🔴 Ein Wert AUSSERHALB der Menge wirft, statt still auf `ready` zu fallen —
 *   dieselbe Bauform wie `EmptyState.jsx:122-124`. Ein Tippfehler in
 *   `optionsStatus` waere sonst unsichtbar: die Flaeche zeigte „No options",
 *   und niemand erfuehre, dass der Aufrufer etwas anderes gemeint hat.
 *
 * @useWhen
 *   - Die Optionsliste einer Facette ist leer und der Grund ist bekannt
 *   - Ein Aufrufer muss den Grund DURCHREICHEN, statt „es gibt nichts" zu raten
 *
 * @dontUseFor
 *   - Eine Flaeche mit Inhalt, Handlung oder Weg heraus — dafuer `EmptyState`
 *     (ein Block passt nicht in ein Popover, und `error` braucht dort einen
 *     Wiederholen-Weg)
 *   - Einen Ladezustand als eigenen Bildschirm — dafuer `Skeletons/`
 *
 * @related
 *   - `EmptyState` — dieselbe Grund-Idee als Block; hier die Zeile im Popover
 *   - `BottomSheetSelect` / `BottomSheetMultiSelect` — die zwei Verbraucher
 *
 * @reuseHints
 *   - Jede weitere Flaeche, die einen Grund in eine ZEILE statt in einen Block legt
 */

// Die vier Gruende. Die Reihenfolge ist die, die ein Aufrufer durchlaeuft:
// geliefert → laedt → Fehler → unbekannt.
export const OPTIONS_GRUENDE = ['ready', 'loading', 'error', 'unknown'];

export const LEER_TEXT = {
  ready: 'No options',
  loading: 'Loading options…',
  error: "Options couldn't be loaded",
  unknown: 'Options not loaded yet',
};

// 🔴 `data-reason` fuehrt die Woerter der Hausform `EmptyState` (G0.2):
// `ready` heisst dort `no-data` („es gibt nichts"), `error` und `unknown`
// heissen gleich. 🔴 `loading` traegt KEIN Wort (R16-W1, Wiedervorlage 4): es
// ist kein Leerzustand im Sinn von §8.1, und ein fuenftes Wort ausserhalb der
// Konzeptmenge machte die Pruefflaeche P9/P33 (`[data-reason]`) unscharf. Der
// SATZ bleibt, `aria-describedby` auch — nur das Wort faellt.
export const GRUND_IM_DOM = {
  ready: 'no-data',
  loading: undefined, // kein Wort — die Zeile traegt kein `data-reason`
  error: 'error',
  unknown: 'unknown',
};

/** Wirft bei einem Wert ausserhalb der Menge. Meldung englisch — neue
 *  Entwicklermeldungen sind englisch (G0.2), der Sprachwaechter ist ein
 *  Ratchet und darf nur sinken. */
export function pruefeOptionsStatus(status) {
  if (!OPTIONS_GRUENDE.includes(status)) {
    throw new Error(
      `optionsStatus: unknown value "${status}". Allowed: ${OPTIONS_GRUENDE.join(', ')}.`
    );
  }
}

/** Der Satz fuer den Grund. Wirft bei unbekanntem Wert. */
export function leerTextFuer(status) {
  pruefeOptionsStatus(status);
  return LEER_TEXT[status];
}

/** Dasselbe in den Woertern von `EmptyState`, fuer `data-reason` — die Lade-Lage hat KEINEN Grund, ihr Wert ist `undefined`. */
export function grundImDom(status) {
  pruefeOptionsStatus(status);
  return GRUND_IM_DOM[status];
}
